import { FC, useState, useEffect } from "react";

import { Field, Action, BackgroundColor } from "../../types";
import { RenderFieldValue, RenderFieldModal, RenderField } from "../../render";
import { SlideOverForm } from "../modal/SlideoverForm";
import { RowLabel } from "./label";
import { RowButton } from "./button";
import { RowPaginator } from "./paginator";
import { useForm } from "../../app/useForm";
import { Button, ButtonType } from "../modal/button";

type RowProps = {
  field: Field;
  usePadding: boolean;
};

export const Row: FC<RowProps> = ({ field, usePadding }) => {
  const [openModal, setOpenModal] = useState(false);
  const [stateField, setStateField] = useState<Field>(field);

  const {
    editable,
    label,
    fields,
    type,
    action,
    name,
    value,
    paginator,
    style,
  } = stateField;

  const { handleFormAccept, postData } = useForm();

  useEffect(() => {
    setStateField(field);
  }, [field]);

  const showEditable = type === "modal" || (type !== "boolean" && editable);
  const showInlineChildren = fields && fields?.length > 0 && type !== "modal";
  const checkValue = name && name in postData ? postData[name] : value;

  let showInlineChildrenFields = false;
  if (showInlineChildren && checkValue === true) {
    showInlineChildrenFields = true;
  }

  let fontSizeClasses = "text-sm";
  if (style?.template === "bold") {
    fontSizeClasses = "text-base font-medium";
  } else if (style?.template === "extrabold") {
    fontSizeClasses = "text-lg font-medium";
  }

  let separatorClasses = "";
  if (style?.separator && style?.separator.direction === "bottom") {
    separatorClasses =
      "pb-5 sm:pb-5 mb-5 sm:mb-5 border-b border-stone-200 dark:border-black";
  }

  const mainWrapperClass = `relative grid ${!usePadding ? "mt-4" : ""} ${
    showEditable && "cursor-pointer hover:bg-stone-100 dark:hover:bg-stone-700"
  } auto-cols-[1fr_minmax(0,20px)] grid-flow-col items-center gap-4 ${separatorClasses} ${fontSizeClasses} transition duration-300 ease-in-out`;

  const handleAccept = async () => {
    let requestAction = action;

    if (!action && field.fields?.length) {
      const firstChild = field.fields[0];
      if (firstChild) requestAction = firstChild.action as Action;
    }

    if (requestAction) return handleFormAccept(requestAction);
    return true;
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const buttonTypeColors: Record<
    BackgroundColor,
    Exclude<ButtonType, "disabled">
  > = {
    blue: "primary",
    gray: "secondary",
    transparent: "tertiary",
    white: "secondary",
    red: "danger",
    black: "ghost",
    green: "primary",
    yellow: "secondary",
  };

  const buttonType = style?.background_color
    ? buttonTypeColors[style?.background_color]
    : "primary";

  return (
    <>
      {type === "button" ? (
        <Button
          title={label ?? "Save"}
          buttonType={buttonType}
          onClick={() => {
            handleFormAccept(action);
          }}
          type="button"
        />
      ) : (
        <div className={mainWrapperClass}>
          {type === "paginator" && paginator ? (
            <RowPaginator paginator={paginator} />
          ) : (
            <div className="grid w-full auto-cols-[minmax(0,auto)] grid-flow-col gap-5">
              <RowLabel label={label} usePadding={usePadding} />

              <dd
                className={`truncate ${
                  usePadding ? "py-5 px-4" : ""
                } text-right text-stone-700 dark:text-stone-300 ${
                  showEditable ? "mr-5 sm:mr-6" : ""
                }`}
              >
                {RenderFieldValue(stateField)}
              </dd>

              {showEditable && <RowButton onClick={() => setOpenModal(true)} />}
            </div>
          )}
        </div>
      )}

      {showInlineChildren && showInlineChildrenFields && (
        <>
          {fields &&
            fields.map((field, index) => RenderField(field, index, true))}
        </>
      )}

      {showEditable && (
        <SlideOverForm
          open={openModal}
          title={label}
          subtitle=""
          onClose={handleClose}
          onAccept={editable || action ? handleAccept : undefined}
          acceptText={action?.label ?? "Apply"}
        >
          {RenderFieldModal(stateField)}
        </SlideOverForm>
      )}
    </>
  );
};
